import { Fragment, useCallback, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bell, Menu as MenuIcon } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  END_PRESENCE,
  ThemeMenuItems,
  ThemeToggleButton,
  useHostAuth,
  useRoles,
} from '../shared/ui';
import { environment } from '../../environments/environment';
import { useSetting } from '../shared/hooks/use-setting';
import { MY_NOTIFICATION_SUMMARY } from '../features/notifications/graphql/queries';
import { COMPANY_NAME } from './graphql';
import { activeMenuPath, isExternal, visibleMenu } from './menu';
import { MobileMenu } from './mobile-menu';

const NAV_ITEM =
  'cursor-pointer rounded bg-transparent px-3 py-1.5 text-sm text-hilevel-slate ' +
  'no-underline hover:bg-hilevel-light';

/** The bell and the theme switch: an icon on its own, no button chrome. */
const ICON_BUTTON =
  'cursor-pointer bg-transparent px-1 text-hilevel-slate no-underline hover:text-hilevel-red';

const DROPDOWN_ITEM =
  'block w-full cursor-pointer bg-transparent px-3 py-1.5 text-left text-sm ' +
  'text-hilevel-slate no-underline data-[focus]:bg-hilevel-offwhite';

/** Long company names are trimmed rather than pushing the menu wide. */
function trimCompanyName(name: string): string {
  const length = 15;
  return name.length > length ? `${name.substring(0, length - 3)}...` : name;
}

/**
 * The application's chrome.
 *
 * What the sections hold, and who may see them, is in `menu.ts` — this only
 * draws it. The unread badge reads the same query the notifications screen
 * writes to, so marking something read empties it without being told: there is
 * one Apollo cache now, where the federated pair needed a service to carry the
 * news from the remote to the host.
 */
export function MenuBar({
  onPrefetchRoute,
}: {
  /** Called when someone points at an item, to start its chunk early. */
  onPrefetchRoute?: (path: string) => void;
} = {}) {
  const { user, isAuthenticated } = useHostAuth();
  const { loginWithRedirect, logout } = useAuth0();
  const roles = useRoles();
  const [endPresence] = useMutation(END_PRESENCE, {
    variables: { app: 'iqd-pro' },
  });
  const [open, setOpen] = useState(false);
  // Stable, because the drawer subscribes a media query to it.
  const closeMenu = useCallback(() => setOpen(false), []);
  const { pathname } = useLocation();

  // Follow-ups and Conversion ship with the new quote document UI, so they
  // share its setting.
  const { value: newQuoteDocument } = useSetting('newQuoteDocumentEnabled');

  const sections = useMemo(
    () => visibleMenu(roles, { newQuoteDocumentEnabled: !!newQuoteDocument }),
    [roles, newQuoteDocument],
  );

  // The one item the current path is on, and the section holding it.
  //
  // Both menus ask this rather than letting `NavLink` decide for itself.
  // `NavLink` marks every item whose route is a prefix of where you are, and
  // several of these are prefixes of each other — standing on
  // `/projects/board` bolded Projects (`/projects`) alongside Board, and
  // in the Admin dropdown Data Management stayed bold across the six screens
  // underneath it. `activeMenuPath` takes the longest match and only that one.
  const active = activeMenuPath(sections, pathname);
  // Named on the closed hamburger, so a phone still says which part of the
  // product it is showing. The desktop row says it with the highlight on an
  // item that is always there; nothing is always there on a phone.
  const activeSection = active?.section.label;

  const { data: notificationData } = useQuery(MY_NOTIFICATION_SUMMARY, { skip: !isAuthenticated });
  const unread: number =
    (notificationData as { myNotificationSummary?: { unreadCount?: number } } | undefined)
      ?.myNotificationSummary?.unreadCount ?? 0;

  const { data: companyData } = useQuery(COMPANY_NAME, {
    variables: { uuid: user?.companyUuid },
    skip: !user?.companyUuid,
  });
  const companyName = (companyData as { distributorByUuid?: { name?: string } } | undefined)
    ?.distributorByUuid?.name;

  return (
    <nav className="border-b border-hilevel-border bg-hilevel-offwhite">
      <div className="flex flex-wrap items-center gap-1 px-4 py-2">
        {/* Opens the drawer in `mobile-menu.tsx` rather than un-hiding the
            row below it, which is why this and that row no longer share a
            piece of state. It carries the section name because a shut menu is
            the only chrome a phone has, and a bare hamburger says nothing
            about where you are. */}
        {sections.length > 0 && (
          <button
            type="button"
            className={`${BUTTON_SECONDARY} lg:hidden`}
            aria-label={activeSection ? `Menu, in ${activeSection}` : 'Menu'}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen(true)}
          >
            <MenuIcon />
            <span className="max-w-[8rem] truncate">{activeSection ?? 'Menu'}</span>
          </button>
        )}

        {/* `hidden lg:flex`, with nothing toggling it: below `lg` this row is
            not a menu that happens to be collapsed, it is the wrong menu. */}
        <div className="hidden flex-wrap items-center gap-1 lg:flex">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive ? `${NAV_ITEM} bg-hilevel-light font-semibold` : NAV_ITEM
            }
          >
            Home
          </NavLink>

          {/* `modal={false}` on every panel below: an open Headless UI menu
              otherwise marks the rest of the page inert, which puts the other
              section buttons out of reach — a click on one only closed the menu
              already open, so switching sections took two. It also drops the
              scroll lock a menu bar has no use for. */}
          {sections.map((section) => (
            <Menu key={section.label}>
              {/* A step darker than the hover colour: once a dropdown is open
                  the pointer has usually left the button for it, so hover said
                  nothing about which section was showing. Background only — a
                  heavier font would shift every section after it along the
                  row. */}
              <MenuButton className={`${NAV_ITEM} data-[open]:bg-hilevel-border`}>
                {section.label} ▾
              </MenuButton>
              <MenuItems
                modal={false}
                anchor="bottom start"
                className="z-30 min-w-[13rem] rounded border border-hilevel-border bg-white py-1 shadow-lg focus:outline-none [--anchor-gap:2px]"
              >
                {section.items.map((item, index) => (
                  <Fragment key={item.to}>
                    {item.startsGroup && index > 0 && (
                      <div className="my-1 border-t border-hilevel-border" role="none" />
                    )}
                    <MenuItem>
                      {isExternal(item.to) ? (
                        <a
                          href={item.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={DROPDOWN_ITEM}
                        >
                          {item.label} ↗
                        </a>
                      ) : (
                        <Link
                          to={item.to}
                          // Pointing at an item, or tabbing onto it, starts its
                          // chunk downloading — usually enough of a head start
                          // that the click has nothing to wait for.
                          onPointerEnter={() => onPrefetchRoute?.(item.to)}
                          onFocus={() => onPrefetchRoute?.(item.to)}
                          aria-current={item === active?.item ? 'page' : undefined}
                          className={
                            item === active?.item ? `${DROPDOWN_ITEM} font-semibold` : DROPDOWN_ITEM
                          }
                        >
                          {item.label}
                        </Link>
                      )}
                    </MenuItem>
                  </Fragment>
                ))}
              </MenuItems>
            </Menu>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Dropped from a phone's bar once signed in, where it is the
              difference between one row and two — the shell draws the banner
              underneath saying the same thing at length. Signed out there is
              no banner and no hamburger either, so it stays and fits. */}
          {environment.testEnvironment && (
            <span
              className={`rounded bg-amber-400 px-2 py-0.5 text-xs font-semibold text-hilevel-slate ${
                isAuthenticated ? 'max-sm:hidden' : ''
              }`}
            >
              Test Environment
            </span>
          )}

          {!isAuthenticated ? (
            <>
              {/* Signed in, the theme is three named rows in the dropdown
                  below. There is no dropdown here, and the pages reachable
                  signed out — support, the terms — still have to be readable
                  in the theme somebody set on their last visit. */}
              <ThemeToggleButton className={ICON_BUTTON} />
              <button
                type="button"
                className={BUTTON_SECONDARY}
                onClick={() => loginWithRedirect({ appState: { returnTo: '/' } })}
              >
                Login
              </button>
              {/* Reachable signed out on purpose: somebody locked out of their
                  account is exactly who needs it. */}
              <Link to="/support" className={`${BUTTON_PRIMARY} no-underline`}>
                Support
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/notifications"
                className={`${ICON_BUTTON} relative`}
                aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
              >
                <Bell />
                {unread > 0 && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-hilevel-red px-1.5 text-[10px] font-semibold text-white">
                    {unread}
                  </span>
                )}
              </Link>

              <Menu>
                {/* The name is dropped below `sm`. A full name plus the
                    hamburger, its section and the bell is more than a phone's
                    width, and what came of that was the bar wrapping onto a
                    second line — so the one part of it that is already said
                    twice, here and at the top of the dropdown, gives way. */}
                <MenuButton
                  className={`${BUTTON_SECONDARY} max-sm:px-2`}
                  aria-label={user?.name ? `Account: ${user.name}` : 'Account'}
                >
                  {user?.picture && (
                    <img
                      src={user.picture}
                      alt=""
                      width={22}
                      height={22}
                      className="rounded-full"
                    />
                  )}
                  <span className="max-sm:hidden">{user?.name}</span>
                </MenuButton>
                <MenuItems
                  modal={false}
                  anchor="bottom end"
                  className="z-30 min-w-[12rem] rounded border border-hilevel-border bg-white py-1 shadow-lg focus:outline-none [--anchor-gap:2px]"
                >
                  {companyName && (
                    <MenuItem>
                      <NavLink to="/company-settings" className={DROPDOWN_ITEM}>
                        {trimCompanyName(companyName)}
                      </NavLink>
                    </MenuItem>
                  )}
                  <MenuItem>
                    <NavLink to="/user-settings" className={DROPDOWN_ITEM}>
                      Your Profile
                    </NavLink>
                  </MenuItem>
                  <MenuItem>
                    <NavLink to="/support" className={DROPDOWN_ITEM}>
                      Support
                    </NavLink>
                  </MenuItem>
                  {roles.includes('internal-user') && (
                    <MenuItem>
                      <NavLink to="/downloads" className={DROPDOWN_ITEM}>
                        Downloads
                      </NavLink>
                    </MenuItem>
                  )}
                  <div className="my-1 border-t border-hilevel-border" role="none" />
                  <p className="px-3 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-hilevel-gray">
                    Appearance
                  </p>
                  <ThemeMenuItems itemClassName={DROPDOWN_ITEM} />
                  <div className="my-1 border-t border-hilevel-border" role="none" />

                  <MenuItem>
                    <button
                      type="button"
                      className={DROPDOWN_ITEM}
                      onClick={async () => {
                        // Awaited, so the roster is told before the redirect
                        // cancels the request — and caught, so a presence
                        // failure can never be the thing that stops somebody
                        // signing out. Going quiet would clear them anyway,
                        // two and a half minutes later.
                        await endPresence().catch(() => undefined);
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                    >
                      Sign out
                    </button>
                  </MenuItem>
                  {/* The version was already here, and it is what somebody
                      wondering what changed reaches for, so it is the link to
                      the release notes rather than a line of its own. */}
                  <MenuItem>
                    <NavLink
                      to="/changelog"
                      className="block border-t border-hilevel-border px-3 pt-1.5 text-xs text-hilevel-gray no-underline data-[focus]:bg-hilevel-offwhite"
                    >
                      {environment.version} · What's New
                    </NavLink>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </>
          )}
        </div>
      </div>

      <MobileMenu
        open={open}
        onClose={closeMenu}
        sections={sections}
        onPrefetchRoute={onPrefetchRoute}
      />
    </nav>
  );
}
